import { useState, useEffect, useRef } from "react";
import { Check, Lock, ChevronDown, ChevronRight, Users, AlertTriangle, Pencil, ArrowLeft } from "lucide-react";
import axios from "axios";
import { getWeekKey } from "@/lib/chartUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_PIN = "1010";

function fmtThursday(weekKey) {
  const d = new Date(weekKey + "T14:00:00");
  return d.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" });
}

function fmtThursdayShort(weekKey) {
  const d = new Date(weekKey + "T14:00:00");
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

function getNextThursday(weekKey) {
  const d = new Date(weekKey + "T14:00:00");
  d.setDate(d.getDate() + 7);
  return d.toISOString().slice(0, 10);
}

export default function AttendanceView({ members }) {
  const currentWeek = getWeekKey();
  const [selectedWeek, setSelectedWeek] = useState(currentWeek);
  const [present, setPresent] = useState([]);
  const [absenceNotes, setAbsenceNotes] = useState({});
  const [absenceSubs, setAbsenceSubs] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const [allRecords, setAllRecords] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [expandedWeek, setExpandedWeek] = useState(null);
  const [absenceView, setAbsenceView] = useState("4weeks");
  const [showAbsences, setShowAbsences] = useState(true);
  const [manualEdit, setManualEdit] = useState(false);

  const nextWeekKey = getNextThursday(currentWeek);
  const isEditingPast = selectedWeek !== currentWeek && selectedWeek !== nextWeekKey;

  useEffect(() => {
    async function load() {
      try {
        const allRes = await axios.get(`${API}/attendance`);
        setAllRecords(allRes.data || []);
        const records = allRes.data || [];
        // If current week already has saved attendance, auto-advance to next week
        // BUT only on initial load, not when user clicked edit
        const currentMatch = records.find(r => r.weekKey === currentWeek);
        if (!manualEdit && currentMatch && currentMatch.present && currentMatch.present.length > 0 && selectedWeek === currentWeek) {
          const nextWeek = getNextThursday(currentWeek);
          setSelectedWeek(nextWeek);
          const nextMatch = records.find(r => r.weekKey === nextWeek);
          setPresent(nextMatch ? nextMatch.present : []);
          setAbsenceNotes(nextMatch?.absenceNotes || {});
          setAbsenceSubs(nextMatch?.absenceSubs || {});
        } else {
          const match = records.find(r => r.weekKey === selectedWeek);
          setPresent(match ? match.present : []);
          setAbsenceNotes(match?.absenceNotes || {});
          setAbsenceSubs(match?.absenceSubs || {});
        }
      } catch { /* */ }
      finally { setLoaded(true); }
    }
    load();
  }, [selectedWeek]);

  const history = allRecords
    .filter(d => d.weekKey !== selectedWeek)
    .sort((a, b) => b.weekKey.localeCompare(a.weekKey));

  function toggleMember(name) {
    setSaved(false);
    setPresent(prev =>
      prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]
    );
  }

  async function saveAttendance() {
    setSaving(true);
    try {
      await axios.post(`${API}/attendance`, {
        pin: ADMIN_PIN,
        weekKey: selectedWeek,
        present,
        absenceNotes,
        absenceSubs,
      });
      setSaved(true);
      const allRes = await axios.get(`${API}/attendance`);
      setAllRecords(allRes.data || []);

      setTimeout(() => {
        setSaved(false);
        setManualEdit(false);
        if (!isEditingPast) {
          const nextWeek = getNextThursday(selectedWeek);
          setSelectedWeek(nextWeek);
          const nextMatch = (allRes.data || []).find(r => r.weekKey === nextWeek);
          setPresent(nextMatch ? nextMatch.present : []);
          setAbsenceNotes(nextMatch?.absenceNotes || {});
          setAbsenceSubs(nextMatch?.absenceSubs || {});
        } else {
          setSelectedWeek(currentWeek);
          const curMatch = (allRes.data || []).find(r => r.weekKey === currentWeek);
          setPresent(curMatch ? curMatch.present : []);
          setAbsenceNotes(curMatch?.absenceNotes || {});
          setAbsenceSubs(curMatch?.absenceSubs || {});
        }
      }, 1200);
    } catch { /* */ }
    finally { setSaving(false); }
  }

  function editWeek(weekKey) {
    setManualEdit(true);
    const match = allRecords.find(r => r.weekKey === weekKey);
    setSelectedWeek(weekKey);
    setPresent(match ? match.present : []);
    setAbsenceNotes(match?.absenceNotes || {});
    setAbsenceSubs(match?.absenceSubs || {});
    setSaved(false);
    document.getElementById("attendance-checklist")?.scrollIntoView({ behavior: "smooth" });
  }

  function goToCurrent() {
    setManualEdit(false);
    setSelectedWeek(currentWeek);
    const match = allRecords.find(r => r.weekKey === currentWeek);
    setPresent(match ? match.present : []);
    setAbsenceNotes(match?.absenceNotes || {});
    setAbsenceSubs(match?.absenceSubs || {});
    setSaved(false);
  }

  function getAbsenceData() {
    const now = new Date();
    let cutoff;
    if (absenceView === "4weeks") {
      cutoff = new Date(now);
      cutoff.setDate(cutoff.getDate() - 28);
    } else {
      cutoff = new Date(now);
      cutoff.setDate(cutoff.getDate() - 91);
    }

    const relevantRecords = allRecords.filter(r => {
      const d = new Date(r.weekKey + "T14:00:00");
      return d >= cutoff;
    }).sort((a, b) => a.weekKey.localeCompare(b.weekKey));

    const totalMeetings = relevantRecords.length;
    const memberAbsences = {};

    for (const name of members) {
      const missed = [];
      for (const rec of relevantRecords) {
        if (!rec.present.includes(name)) {
          const note = rec.absenceNotes?.[name] || "";
          const sub = rec.absenceSubs?.[name] || "";
          missed.push({ weekKey: rec.weekKey, note, sub });
        }
      }
      memberAbsences[name] = { missed, count: missed.length, total: totalMeetings };
    }

    return { memberAbsences, totalMeetings };
  }

  const sortedMembers = [...members].sort();
  const presentCount = present.length;
  const totalCount = sortedMembers.length;
  const { memberAbsences, totalMeetings } = getAbsenceData();

  return (
    <div data-testid="attendance-view" id="attendance-checklist">
      {isEditingPast && (
        <div className="flex items-center gap-3 px-4 py-2.5 mb-4 bg-amber-50 border border-amber-200 rounded-lg">
          <Pencil className="w-4 h-4 text-amber-600 flex-shrink-0" />
          <p className="text-xs font-semibold text-amber-800 flex-1">Editing past week — {fmtThursday(selectedWeek)}</p>
          <button onClick={goToCurrent} className="inline-flex items-center gap-1 px-3 py-1 text-[11px] font-bold text-amber-700 bg-amber-100 hover:bg-amber-200 rounded-md transition-colors">
            <ArrowLeft className="w-3 h-3" /> Back to This Week
          </button>
        </div>
      )}

      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h3 className="text-base font-bold text-zinc-900">{fmtThursday(selectedWeek)}</h3>
          <p className="text-xs text-zinc-500">{presentCount} of {totalCount} present</p>
        </div>
        <button
          data-testid="save-attendance-btn"
          onClick={saveAttendance}
          disabled={saving}
          className={`inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold rounded-lg transition-colors ${
            saved ? "bg-green-100 text-green-700 border border-green-200" : "bg-zinc-900 text-white hover:bg-zinc-800"
          }`}
        >
          {saving ? "Saving..." : saved ? <><Check className="w-3.5 h-3.5" /> Saved</> : isEditingPast ? "Update Attendance" : "Save Attendance"}
        </button>
      </div>

      {loaded ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mb-6">
          {sortedMembers.map(name => {
            const isPresent = present.includes(name);
            return (
              <div key={name} className="space-y-0">
                <button
                  data-testid={`attendance-${name}`}
                  onClick={() => toggleMember(name)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border-2 text-left transition-all ${
                    isPresent ? "bg-green-50 border-green-400 text-green-800" : "bg-white border-zinc-200 text-zinc-500 hover:border-zinc-300"
                  } ${!isPresent ? "rounded-b-none border-b" : ""}`}
                >
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                    isPresent ? "bg-green-500 text-white" : "bg-zinc-100"
                  }`}>
                    {isPresent ? <Check className="w-4 h-4" /> : <span className="w-4 h-4" />}
                  </div>
                  <span className={`text-sm font-semibold ${isPresent ? "text-green-800" : "text-zinc-600"}`}>{name}</span>
                </button>
                {!isPresent && (
                  <div className="border-2 border-t-0 border-zinc-200 rounded-b-lg bg-zinc-50 px-3 py-2 space-y-1.5">
                    <div className="flex gap-1.5">
                      <button
                        onClick={() => { setSaved(false); setAbsenceSubs(prev => ({ ...prev, [name]: "sub" })); }}
                        className={`flex-1 py-1.5 text-[11px] font-bold rounded-md transition-colors ${
                          absenceSubs[name] === "sub" ? "bg-blue-600 text-white" : "bg-white border border-zinc-200 text-zinc-500 hover:border-zinc-300"
                        }`}
                      >
                        Brought a Sub
                      </button>
                      <button
                        onClick={() => { setSaved(false); setAbsenceSubs(prev => ({ ...prev, [name]: "nosub" })); }}
                        className={`flex-1 py-1.5 text-[11px] font-bold rounded-md transition-colors ${
                          absenceSubs[name] === "nosub" ? "bg-red-500 text-white" : "bg-white border border-zinc-200 text-zinc-500 hover:border-zinc-300"
                        }`}
                      >
                        No Sub
                      </button>
                    </div>
                    <input
                      value={absenceNotes[name] || ""}
                      onChange={e => {
                        setSaved(false);
                        setAbsenceNotes(prev => ({ ...prev, [name]: e.target.value }));
                      }}
                      placeholder="Reason (optional)..."
                      className="w-full px-2 py-1.5 text-[11px] border border-zinc-200 rounded-md bg-white outline-none focus:border-zinc-300 text-zinc-600 placeholder:text-zinc-300"
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flex justify-center py-8">
          <div className="w-6 h-6 border-3 border-zinc-300 border-t-zinc-600 rounded-full animate-spin" />
        </div>
      )}

      {/* Absence Report */}
      <div className="border-t border-zinc-200 pt-4 mb-6">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <button onClick={() => setShowAbsences(!showAbsences)} className="flex items-center gap-2 text-sm font-bold text-zinc-700 hover:text-zinc-900">
            {showAbsences ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            Absence Report
          </button>
          {showAbsences && (
            <div className="flex gap-1">
              {["4weeks", "quarter"].map(v => (
                <button key={v} onClick={() => setAbsenceView(v)} className={`px-3 py-1 text-[11px] font-bold rounded-md transition-colors ${absenceView === v ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}>
                  {v === "4weeks" ? "Last 4 Weeks" : "Quarter"}
                </button>
              ))}
            </div>
          )}
        </div>
        {showAbsences && (
          totalMeetings === 0 ? (
            <p className="text-xs text-zinc-400 italic py-4">No attendance records yet. Save this week's attendance to start tracking.</p>
          ) : (
            <div className="space-y-2">
              {sortedMembers.map(name => {
                const data = memberAbsences[name];
                if (!data || data.count === 0) return (
                  <div key={name} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-green-50 border border-green-100">
                    <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                    <span className="text-sm font-semibold text-green-800 flex-1">{name}</span>
                    <span className="text-[11px] font-bold text-green-600">{data.total}/{data.total} present</span>
                  </div>
                );
                const pct = Math.round((data.count / data.total) * 100);
                return (
                  <div key={name} className={`rounded-lg border overflow-hidden ${pct >= 50 ? "border-red-200 bg-red-50" : pct >= 25 ? "border-amber-200 bg-amber-50" : "border-zinc-200 bg-white"}`}>
                    <div className="flex items-center gap-3 px-3 py-2">
                      <AlertTriangle className={`w-4 h-4 flex-shrink-0 ${pct >= 50 ? "text-red-500" : pct >= 25 ? "text-amber-500" : "text-zinc-400"}`} />
                      <span className="text-sm font-semibold text-zinc-800 flex-1">{name}</span>
                      <span className={`text-[11px] font-bold ${pct >= 50 ? "text-red-600" : pct >= 25 ? "text-amber-600" : "text-zinc-500"}`}>{data.count} missed of {data.total}</span>
                    </div>
                    <div className="px-3 pb-2 flex flex-wrap gap-1">
                      {data.missed.map(m => (
                        <span key={m.weekKey} className={`px-2 py-0.5 text-[10px] font-semibold rounded border ${
                          m.sub === "sub" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-white/70 text-red-600 border-red-200"
                        }`}>
                          {fmtThursdayShort(m.weekKey)}{m.sub === "sub" ? " (sub)" : ""}{m.note ? ` — ${m.note}` : ""}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )
        )}
      </div>

      {/* Past Attendance */}
      {history.length > 0 && (
        <div className="border-t border-zinc-200 pt-4">
          <button onClick={() => setShowHistory(!showHistory)} className="flex items-center gap-2 text-sm font-bold text-zinc-700 hover:text-zinc-900 mb-3">
            {showHistory ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            Past Attendance ({history.length} weeks)
          </button>
          {showHistory && (
            <div className="space-y-2">
              {history.map(rec => (
                <div key={rec.weekKey} className="border border-zinc-200 rounded-lg overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 hover:bg-zinc-50 transition-colors">
                    <button onClick={() => setExpandedWeek(expandedWeek === rec.weekKey ? null : rec.weekKey)} className="flex items-center gap-2 flex-1 text-left">
                      <Users className="w-4 h-4 text-zinc-400" />
                      <span className="text-sm font-semibold text-zinc-800">{fmtThursday(rec.weekKey)}</span>
                      <span className="text-xs font-bold text-zinc-500 ml-auto mr-2">{rec.present.length} / {totalCount}</span>
                    </button>
                    <button onClick={() => editWeek(rec.weekKey)} className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-bold text-zinc-600 bg-zinc-100 hover:bg-zinc-200 rounded-md transition-colors flex-shrink-0">
                      <Pencil className="w-3 h-3" /> Edit
                    </button>
                  </div>
                  {expandedWeek === rec.weekKey && (
                    <div className="px-4 pb-3 flex flex-wrap gap-1.5">
                      {sortedMembers.map(name => {
                        const isP = rec.present.includes(name);
                        const note = rec.absenceNotes?.[name];
                        const sub = rec.absenceSubs?.[name];
                        return (
                          <span key={name} className={`px-2.5 py-1 text-[11px] font-semibold rounded-full ${
                            isP ? "bg-green-100 text-green-700" :
                            sub === "sub" ? "bg-blue-100 text-blue-600" :
                            "bg-red-50 text-red-400 line-through"
                          }`}>
                            {name}{!isP && sub === "sub" ? " (sub)" : ""}{!isP && note ? ` · ${note}` : ""}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
